import React, { Component } from 'react';
import { Container, Row, Col, Card, Button, Alert, Form, Badge, ButtonGroup } from 'react-bootstrap';
import { GoCalendar } from 'react-icons/go';
import { MdOutlineExpandMore, MdRefresh } from "react-icons/md";
import { AgGridReact } from 'ag-grid-react';
import { GoX, GoInfo } from "react-icons/go";
import Navigation from 'context/nav';
import Utils from 'context/utils';
import Preferences from 'context/preferences';
import ActionCard, { ActionCards } from 'blob_types/blobhub/compute/workflow/controls/common/actionCard';
import { aliasCellRenderer } from 'components/controls/aliasCellRenderer';
import { scheduleStateBadgeClass } from 'blob_types/blobhub/compute/scheduler/schedules/badgeClass';
import { formatInvocationTarget } from 'blob_types/blobhub/compute/scheduler/schedules/invocationTarget';
import ScheduleModal from './create';

export default class ScheduleListView extends Component {
  constructor(props) {
    super(props);
    const scope = { orgId: this.props.blob.org_id, blobId: this.props.blob.id };

    this.state = {
      details: Preferences.get("blob:scheduler.scheduleDetailsPane", scope) ?? true,
      schedules: null,
      lastScheduleId: null,
      createScheduleModalShow: false,
      progress: false,
      error: false,
      message: null,
    };
  }

  componentDidMount() {
    this.loadSchedules(false);
  }

  updateProgress = (progress, error, message) => {
    this.setState({
      progress: progress,
      error: error,
      message: message
    });
  }

  loadSchedules(more) {
    this.updateProgress(true, false, "Loading schedules...");

    const body = {
      command: "list_schedules"
    };
    if ( more ) {
      body["start_schedule_id"] = this.state.lastScheduleId;
    }
    this.props.app.api.revisionsIdDataQueryPost(this.props.revision.id, body)
      .then( response => {
        this.updateProgress(false, false, null);

        const schedules = response.data.schedules;
        const lastScheduleId = response.data.last_schedule_id;

        if ( more ) {
          schedules.push(...this.state.schedules);
        }

        this.setState({
          schedules: schedules,
          lastScheduleId: lastScheduleId
        });
      }, error => {
        this.updateProgress(false, true, "Failed to fetch the list of schedules: " + error.message);
      });
  }

  createSchedule(schedule) {
    this.updateProgress(true, false, "Creating a new schedule...");

    const body = {
      "command": "create_schedule",
      "enabled": schedule.enabled,
      "repeat": schedule.repeat,
      "timezone": schedule.timezone,
      "invocation_target_type": "workflow",
      "invocation_target": schedule.invocationTarget
    };
    if ( "recurring_cron" === schedule.repeat ) {
      body["cron_expression"] = schedule.cronExpression;
    } else {
      body["invocation_time"] = schedule.invocationTime;
    }
    if ( schedule.startDate ) {
      body["start_date"] = schedule.startDate;
    }
    if ( schedule.endDate ) {
      body["end_date"] = schedule.endDate;
    }
    if ( schedule.alias && schedule.alias.trim() ) {
      body["alias"] = schedule.alias.trim();
    }
    if ( schedule.description ) {
      body["description"] = schedule.description;
    }
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        this.updateProgress(false, false, null);
        this.setState({ createScheduleModalShow: false });
        this.loadSchedules(false);
      }, error => {
        this.updateProgress(false, true, "Failed to create schedule: " + error.message);
      });
  }

  proceedToSchedule(scheduleId) {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    this.props.history.push(Navigation.schedulerPath(orgId, blobId, "schedules", scheduleId));
  }

  formatTarget(schedule) {
    if ( !schedule.invocation_target ) {
      return <span className="text-muted">No target</span>;
    }
    return formatInvocationTarget(schedule);
  }

  render() {
    const columns = [
      {
        "field": "id",
        "headerName": "Schedule ID",
        "filter": true,
        "flex": 4,
        "sortable": false,
        cellRenderer: params => {
          return (
            <a href="#" onClick={(event) => {
              event.preventDefault();
              this.proceedToSchedule(params.value);
            }}>{params.value}</a>
          );
        }
      },
      {
        "field": "alias",
        "headerName": "Alias",
        "filter": true,
        "flex": 4,
        "sortable": true,
        cellRenderer: aliasCellRenderer
      },
      {
        "field": "enabled",
        "headerName": "Enabled",
        "filter": true,
        "flex": 2,
        "sortable": true,
        cellRenderer: params => {
          // A var() string, not a resolved colour: React writes it onto the element's inline style
          // and the browser re-resolves it when the theme flips, with no re-render of the grid.
          const color = params.value ? "var(--bh-status-enabled)" : "var(--bh-status-disabled)";
          const label = params.value ? "Yes" : "No";
          return ( <><span style={{color: color}}>&#9679;</span> {label}</> );
        }
      },
      {
        "field": "repeat",
        "headerName": "Repeat",
        "filter": true,
        "flex": 3,
        "sortable": true,
        cellRenderer: params => {
          if ( "recurring_cron" === params.value ) {
            return ( <>Recurring</> );
          } else if ( "one_time" === params.value ) {
            return ( <>One-time</> );
          }
          return ( <>{params.value}</> );
        }
      },
      {
        "field": "expression_or_time",
        "headerName": "Expression / Time",
        "filter": true,
        "flex": 5,
        "sortable": false,
        valueGetter: params => {
          if ( params.data ) {
            return params.data.cron_expression || params.data.invocation_time || "";
          }
          return "";
        },
        cellRenderer: params => {
          if ( params.data && params.data.cron_expression ) {
            return ( <code>{params.data.cron_expression}</code> );
          }
          if ( params.data && params.data.invocation_time ) {
            return ( <>{Utils.formatNaiveDateTime(params.data.invocation_time)}</> );
          }
          return null;
        }
      },
      {
        "field": "timezone",
        "headerName": "Timezone",
        "filter": true,
        "flex": 4,
        "sortable": true,
      },
      {
        "field": "state",
        "headerName": "State",
        "filter": true,
        "flex": 2,
        "sortable": true,
        cellRenderer: params => {
          return ( <Badge className={scheduleStateBadgeClass(params.value)}>{params.value}</Badge> );
        }
      },
      {
        "field": "invocation_target",
        "headerName": "Target",
        "filter": false,
        "flex": 4,
        "sortable": false,
        cellRenderer: params => {
          return ( <>{this.formatTarget(params.data)}</> );
        }
      }
    ];

    return (
      <>
        {( this.state.progress || this.state.error ) &&
          <Container>
            <Row>
              <Col>
                <Form.Group>
                  <Alert variant={this.state.progress ? "primary" : "warning"}>
                    {this.state.message}
                  </Alert>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        }
        <Container className={this.state.details ? "container-wide" : ""}>
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  Schedules
                  <span className="float-right">
                    {this.state.schedules &&
                      <>
                        Total fetched: <strong>{this.state.schedules.length}</strong>
                        {this.state.lastScheduleId &&
                          <> | More available</>
                        }
                      </>
                    }
                    {!this.state.details &&
                      <ButtonGroup size="sm" className="header-options">
                        &nbsp;&nbsp;
                        <Button variant="light" onClick={() => { this.setState({ details: true }) }}><GoInfo/></Button>
                      </ButtonGroup>
                    }
                  </span>
                </Card.Header>
                <Card.Body>
                  <Card.Text>
                    <div className="ag-theme-quartz">
                      <AgGridReact
                        rowData={this.state.schedules}
                        columnDefs={columns}
                        domLayout='autoHeight'
                        pagination={false}
                      />
                    </div>
                  </Card.Text>
                  {!this.state.progress &&
                    <Card.Text>
                      {this.state.lastScheduleId &&
                        <Button variant="light" size="sm" className="mr-3" onClick={() => {
                          this.loadSchedules(true);
                        }}><MdOutlineExpandMore /> Load More Results</Button>
                      }
                      <Button variant="light" size="sm" onClick={() => {
                        this.loadSchedules(false);
                      }}><MdRefresh /> Refresh</Button>
                    </Card.Text>
                  }
                </Card.Body>
              </Card>
            </Col>
            <Col sm={4} className={`detail-panel-col ${this.state.details ? "" : "hidden"}`}>
              <Card>
                <Card.Header>
                  Overview
                  <span className="float-right">
                    <ButtonGroup size="sm" className="header-options">
                      <Button variant="light" onClick={() => { this.setState({ details: false }) }}><GoX/></Button>
                    </ButtonGroup>
                  </span>
                </Card.Header>
                <Card.Body>
                  <Card.Text as="h6">General</Card.Text>
                  <hr />
                  <Card.Text>
                    Schedules trigger workflow executions on a recurring cron schedule or at a one-time invocation time.
                    Each schedule targets a specific workflow execution and stores its execution history.
                  </Card.Text>

                  {(!this.state.progress && this.props.canEditRevision ) &&
                    <>
                      <Card.Text as="h6">Actions</Card.Text>
                      <ActionCards>
                        <ActionCard helper="Creates a new schedule in this revision">
                          <Button variant="success" size="sm" onClick={() => {
                            this.setState({ createScheduleModalShow: true });
                          }}><GoCalendar /> New Schedule</Button>
                        </ActionCard>
                      </ActionCards>
                    </>
                  }
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        <ScheduleModal
          show={this.state.createScheduleModalShow}
          app={this.props.app}
          orgId={this.props.blob.org_id}
          onHide={() => this.setState({ createScheduleModalShow: false })}
          handleSubmit={(schedule) => this.createSchedule(schedule)}
        />
      </>
    );
  }
}
